import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm, useFieldArray } from 'react-hook-form';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useClients } from '../hooks/useClients';
import { useServiceTemplates } from '../hooks/useInvoices';

const { FiX, FiPlus, FiTrash2, FiSave, FiLoader, FiCalendar, FiDollarSign } = FiIcons;

const InvoiceModal = ({ isOpen, onClose, invoice = null, onSubmit }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { clients } = useClients();
  const { templates } = useServiceTemplates();
  
  const { register, handleSubmit, formState: { errors }, reset, control, watch, setValue } = useForm({
    defaultValues: {
      client_id: '',
      invoice_type: 'one_time',
      status: 'draft',
      issue_date: new Date().toISOString().split('T')[0],
      due_date: '',
      services: [
        {
          name: '',
          description: '',
          quantity: 1,
          rate: 0,
          amount: 0,
          tax_rate: 18
        }
      ],
      ad_spend: 0,
      payment_terms: 'Net 30 days',
      notes: '',
      client_location: 'same_state'
    }
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "services"
  });

  const watchedServices = watch('services');
  const watchedClientId = watch('client_id');
  const watchedInvoiceType = watch('invoice_type');

  // Calculate totals when services change
  useEffect(() => {
    const selectedClient = clients.find(c => c.id.toString() === watchedClientId);
    const clientGstType = selectedClient?.gst_type || 'B2B';
    
    watchedServices.forEach((service, index) => {
      const quantity = parseFloat(service.quantity) || 0;
      const rate = parseFloat(service.rate) || 0;
      const amount = quantity * rate;
      setValue(`services.${index}.amount`, amount);
    });
  }, [watchedServices, watchedClientId, setValue]);

  // Set due date based on issue date and payment terms
  useEffect(() => {
    const issueDate = watch('issue_date');
    const paymentTerms = watch('payment_terms');
    
    if (issueDate && paymentTerms) {
      const days = parseInt(paymentTerms.match(/\d+/)?.[0]) || 30;
      const dueDate = new Date(issueDate);
      dueDate.setDate(dueDate.getDate() + days);
      setValue('due_date', dueDate.toISOString().split('T')[0]);
    }
  }, [watch('issue_date'), watch('payment_terms'), setValue]);

  useEffect(() => {
    if (invoice) {
      reset({
        client_id: invoice.client_id?.toString() || '',
        invoice_type: invoice.invoice_type || 'one_time',
        status: invoice.status || 'draft',
        issue_date: invoice.issue_date || new Date().toISOString().split('T')[0],
        due_date: invoice.due_date || '',
        services: invoice.services || [
          {
            name: '',
            description: '',
            quantity: 1,
            rate: 0,
            amount: 0,
            tax_rate: 18
          }
        ],
        ad_spend: invoice.ad_spend || 0,
        payment_terms: invoice.payment_terms || 'Net 30 days',
        notes: invoice.notes || '',
        client_location: invoice.client_location || 'same_state'
      });
    } else {
      reset({
        client_id: '',
        invoice_type: 'one_time',
        status: 'draft',
        issue_date: new Date().toISOString().split('T')[0],
        due_date: '',
        services: [
          {
            name: '',
            description: '',
            quantity: 1,
            rate: 0,
            amount: 0,
            tax_rate: 18
          }
        ],
        ad_spend: 0,
        payment_terms: 'Net 30 days',
        notes: '',
        client_location: 'same_state'
      });
    }
  }, [invoice, reset]);

  const handleFormSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      const selectedClient = clients.find(c => c.id.toString() === data.client_id);
      await onSubmit({
        ...data,
        client: selectedClient,
        client_id: parseInt(data.client_id)
      });
      onClose();
    } catch (error) {
      console.error('Error submitting invoice:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const addServiceFromTemplate = (templateId) => {
    const template = templates.find(t => t.id === parseInt(templateId));
    if (template) {
      append({
        name: template.name,
        description: template.description,
        quantity: template.billing_type === 'hourly' ? 10 : 1,
        rate: template.default_rate,
        amount: template.billing_type === 'hourly' ? template.default_rate * 10 : template.default_rate,
        tax_rate: template.tax_rate
      });
    }
  };

  const selectedClient = clients.find(c => c.id.toString() === watchedClientId);
  const subtotal = watchedServices.reduce((sum, service) => sum + (parseFloat(service.amount) || 0), 0);
  const isB2C = selectedClient?.gst_type === 'B2C';
  const totalTax = isB2C ? 0 : watchedServices.reduce((sum, service) => {
    const taxRate = parseFloat(service.tax_rate) || 0;
    const amount = parseFloat(service.amount) || 0;
    return sum + (amount * taxRate / 100);
  }, 0);
  const totalAmount = subtotal + totalTax;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          
          <motion.div
            className="relative bg-white rounded-xl w-full max-w-6xl max-h-[95vh] overflow-y-auto shadow-xl"
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ duration: 0.3 }}
          >
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-xl z-10">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-gray-800">
                    {invoice ? 'Edit Invoice' : 'Create New Invoice'}
                  </h2>
                  <p className="text-sm text-gray-600">
                    {invoice ? 'Update invoice details and services' : 'Generate professional invoices with GST compliance'}
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                  disabled={isSubmitting}
                >
                  <SafeIcon icon={FiX} className="w-5 h-5" />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit(handleFormSubmit)} className="p-6 space-y-8">
              {/* Invoice Header */}
              <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <SafeIcon icon={FiIcons.FiFileText} className="w-5 h-5 mr-2" />
                  Invoice Details
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Client *
                    </label>
                    <select
                      {...register('client_id', { required: 'Client is required' })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Select Client</option>
                      {clients.map(client => (
                        <option key={client.id} value={client.id}>
                          {client.business_name} ({client.name})
                        </option>
                      ))}
                    </select>
                    {errors.client_id && (
                      <p className="text-red-500 text-sm mt-1">{errors.client_id.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Invoice Type
                    </label>
                    <select
                      {...register('invoice_type')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="one_time">One-Time Invoice</option>
                      <option value="recurring">Recurring Invoice</option>
                      <option value="ad_spend">Ad Spend Invoice</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <select
                      {...register('status')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="draft">Draft</option>
                      <option value="sent">Sent</option>
                      <option value="paid">Paid</option>
                      <option value="overdue">Overdue</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Issue Date *
                    </label>
                    <input
                      type="date"
                      {...register('issue_date', { required: 'Issue date is required' })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    {errors.issue_date && (
                      <p className="text-red-500 text-sm mt-1">{errors.issue_date.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Due Date *
                    </label>
                    <input
                      type="date"
                      {...register('due_date', { required: 'Due date is required' })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    {errors.due_date && (
                      <p className="text-red-500 text-sm mt-1">{errors.due_date.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Payment Terms
                    </label>
                    <select
                      {...register('payment_terms')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="Net 15 days">Net 15 days</option>
                      <option value="Net 30 days">Net 30 days</option>
                      <option value="Net 45 days">Net 45 days</option>
                      <option value="Net 60 days">Net 60 days</option>
                      <option value="Due on receipt">Due on receipt</option>
                    </select>
                  </div>
                </div>

                {selectedClient && (
                  <div className="mt-4 p-4 bg-white rounded-lg border border-blue-300">
                    <h4 className="font-semibold text-gray-700 mb-2">Client Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <p><span className="font-medium">Business:</span> {selectedClient.business_name}</p>
                        <p><span className="font-medium">Contact:</span> {selectedClient.name}</p>
                        <p><span className="font-medium">Email:</span> {selectedClient.email}</p>
                      </div>
                      <div>
                        <p><span className="font-medium">GST Type:</span> {selectedClient.gst_type}</p>
                        {selectedClient.gstin && (
                          <p><span className="font-medium">GSTIN:</span> {selectedClient.gstin}</p>
                        )}
                        <p><span className="font-medium">Phone:</span> {selectedClient.phone}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Services Section */}
              <div className="bg-green-50 rounded-lg p-6 border border-green-200">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                    <SafeIcon icon={FiIcons.FiList} className="w-5 h-5 mr-2" />
                    Services & Line Items
                  </h3>
                  <div className="flex items-center space-x-2">
                    <select
                      onChange={(e) => e.target.value && addServiceFromTemplate(e.target.value)}
                      className="px-3 py-1 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      value=""
                    >
                      <option value="">Add from template</option>
                      {templates.map(template => (
                        <option key={template.id} value={template.id}>
                          {template.name} (${template.default_rate})
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => append({
                        name: '',
                        description: '',
                        quantity: 1,
                        rate: 0,
                        amount: 0,
                        tax_rate: selectedClient?.gst_type === 'B2C' ? 0 : 18
                      })}
                      className="flex items-center space-x-2 px-3 py-1 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200"
                    >
                      <SafeIcon icon={FiPlus} className="w-4 h-4" />
                      <span>Add Service</span>
                    </button>
                  </div>
                </div>

                <div className="space-y-4">
                  {fields.map((field, index) => (
                    <div key={field.id} className="grid grid-cols-1 md:grid-cols-12 gap-4 p-4 border border-gray-200 rounded-lg bg-white">
                      <div className="md:col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Service Name *
                        </label>
                        <input
                          type="text"
                          {...register(`services.${index}.name`, { required: 'Service name is required' })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                          placeholder="Enter service name"
                        />
                      </div>

                      <div className="md:col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Description
                        </label>
                        <input
                          type="text"
                          {...register(`services.${index}.description`)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                          placeholder="Service description"
                        />
                      </div>

                      <div className="md:col-span-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Qty *
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          {...register(`services.${index}.quantity`, { 
                            required: 'Quantity is required',
                            min: { value: 0.01, message: 'Quantity must be greater than 0' }
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        />
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Rate *
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          {...register(`services.${index}.rate`, { 
                            required: 'Rate is required',
                            min: { value: 0, message: 'Rate cannot be negative' }
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        />
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Amount
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          {...register(`services.${index}.amount`)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 focus:outline-none"
                          readOnly
                        />
                      </div>

                      <div className="md:col-span-1 flex items-end">
                        <button
                          type="button"
                          onClick={() => remove(index)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                          disabled={fields.length === 1}
                        >
                          <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Invoice Totals */}
                <div className="mt-6 bg-white rounded-lg p-4 border border-green-300">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      {watchedInvoiceType === 'ad_spend' && (
                        <div className="mb-4">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Ad Spend Amount
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            {...register('ad_spend', { min: { value: 0, message: 'Ad spend cannot be negative' } })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                            placeholder="0.00"
                          />
                        </div>
                      )}
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Client Location (for GST)
                        </label>
                        <select
                          {...register('client_location')}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        >
                          <option value="same_state">Same State (CGST + SGST)</option>
                          <option value="different_state">Different State (IGST)</option>
                        </select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="font-medium">Subtotal:</span>
                        <span>${subtotal.toFixed(2)}</span>
                      </div>
                      
                      {!isB2C && (
                        <>
                          {watch('client_location') === 'same_state' ? (
                            <>
                              <div className="flex justify-between text-sm">
                                <span>CGST ({(watchedServices[0]?.tax_rate || 18) / 2}%):</span>
                                <span>${(totalTax / 2).toFixed(2)}</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span>SGST ({(watchedServices[0]?.tax_rate || 18) / 2}%):</span>
                                <span>${(totalTax / 2).toFixed(2)}</span>
                              </div>
                            </>
                          ) : (
                            <div className="flex justify-between text-sm">
                              <span>IGST ({watchedServices[0]?.tax_rate || 18}%):</span>
                              <span>${totalTax.toFixed(2)}</span>
                            </div>
                          )}
                          
                          <div className="flex justify-between font-medium">
                            <span>Total Tax:</span>
                            <span>${totalTax.toFixed(2)}</span>
                          </div>
                        </>
                      )}
                      
                      {isB2C && (
                        <div className="text-sm text-gray-600 italic">
                          No GST applicable (B2C Client)
                        </div>
                      )}
                      
                      <div className="border-t pt-2">
                        <div className="flex justify-between text-lg font-bold">
                          <span>Total Amount:</span>
                          <span>${totalAmount.toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Additional Information */}
              <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  Additional Information
                </h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes & Terms
                  </label>
                  <textarea
                    {...register('notes')}
                    rows="4"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Additional notes, terms and conditions, or special instructions..."
                  />
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex space-x-3 pt-6 border-t border-gray-200 sticky bottom-0 bg-white">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200 font-medium"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200 font-medium flex items-center justify-center space-x-2"
                >
                  {isSubmitting ? (
                    <>
                      <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin" />
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <SafeIcon icon={FiSave} className="w-4 h-4" />
                      <span>{invoice ? 'Update Invoice' : 'Create Invoice'}</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default InvoiceModal;